// CadLoader.js

import { EmaLoader } from "../../../../wgpurenderer/emaLoader.js";

function extractReferencePoint(xmlText) {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, "text/xml");

  const macroVariant = xml.querySelector('MacroVariant[MacroFuncType="10"]');

  if (!macroVariant) {
    return { x: 0, y: 0, z: 0 };
  }

  const value = macroVariant.getAttribute("ReferencePoint");

  if (!value) {
    return { x: 0, y: 0, z: 0 };
  }

  const [x, y, z] = value.split("/").map(Number);

  return { x, y, z };
}

// EmaCadLoader.js
const cadCache = new Map(); // url -> Promise<{ template, referencePoint }>

export async function loadEmaCadModel(
  renderer,
  allocator,
  url,
  removeChild = false,
  { variantIndex = 0 } = {},
) {
  let cached = cadCache.get(url);

  if (!cached) {
    cached = (async () => {
      const xmlText = await fetch(url).then((r) => r.text());
      const referencePoint = extractReferencePoint(xmlText);

      const loader = new EmaLoader(renderer, allocator);
      const node = await loader.loadEma(url, { disableTexts: true });

      if (removeChild) {
        keepOnlyVariant(node, variantIndex);
      }

      // ⚠️ The loader may attach `node` into the renderer's scene graph
      // as a side effect of building it (this is what was showing up as
      // a stray copy sitting at 0,0). This template must NEVER be
      // rendered directly — only cloned via cloneCadNode — so detach
      // and hide it immediately after creation, before caching it.
      node.visible = false;
      if (renderer.sceneRootNode?.removeChild) {
        try {
          renderer.sceneRootNode.removeChild(node);
        } catch (err) {
          // no-op if it wasn't actually attached there — safe to ignore
        }
      }

      return { template: node, referencePoint };
    })();

    cadCache.set(url, cached);
  }

  const { template, referencePoint } = await cached;

  // Cheap re-instantiation: new nodes, same already-uploaded geometry.
  const node = cloneCadNode(renderer, template);

  return { node, referencePoint };
}

// Rebuilds the tree shape using the renderer's own node factories,
// reusing each source node's geometryId (no new storeGeometry calls).
function cloneCadNode(renderer, source) {
  const hasGeometry = source.geometryId !== undefined;
  const node = hasGeometry
    ? renderer.createGraphicalNode()
    : renderer.createInternalNode();

  node.name = source.name;

  if (source.matrix) {
    node.matrix = source.matrix.slice ? source.matrix.slice() : source.matrix;
  }

  if (hasGeometry) {
    node.geometryId = source.geometryId;
    // userData (normalGeometryId/highlightGeometryId) gets set later by
    // prepareHighlight on this specific clone — don't copy it here, since
    // the highlight cache (below) is what actually avoids re-uploading.
  }

  for (const child of source.children || []) {
    node.addChild(cloneCadNode(renderer, child));
  }

  return node;
}

// export async function loadEmaCadModel(
//   renderer,
//   allocator,
//   url,
//   removeChild = false,
//   { variantIndex = 0 } = {},
// ) {
//   // Read EMA XML
//   const xmlText = await fetch(url).then((r) => r.text());

//   const referencePoint = extractReferencePoint(xmlText);

//   // Load model normally
//   const loader = new EmaLoader(renderer, allocator);
//   const node = await loader.loadEma(url, {
//     disableTexts: true,
//   });
//   // and check if renderer or loader has something like:
//   if (removeChild) {
//     keepOnlyVariant(node, variantIndex);
//   }

//   return { node, referencePoint };
// }

// Walks the tree; on any node with more than one child, keeps only
// the child at `variantIndex` and removes the rest.
// Only touches the direct children of `node` — keeps the child at
// variantIndex, removes the rest. No recursion.
function keepOnlyVariant(node, variantIndex) {
  const kids = node.children || [];

  if (kids.length <= 1) return;
  console.log("kids: ", kids);
  // snapshot first — removeChild mutates the live list, so we can't
  // iterate node.children directly while removing from it
  const toRemove = kids.filter((_, i) => i !== variantIndex);
  toRemove.forEach((child) => node.removeChild(child));
}

function transformPoint(m, [x, y, z]) {
  return [
    m[0] * x + m[4] * y + m[8] * z + m[12],
    m[1] * x + m[5] * y + m[9] * z + m[13],
    m[2] * x + m[6] * y + m[10] * z + m[14],
  ];
}

export function getNodeBounds(node, renderer, { debug = false } = {}) {
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  let center = [0, 0, 0];
  const contributingNodes = []; // NEW

  function visit(n) {
    const bb = n.boundingBox;
    if (bb) {
      const [cx, cy, cz] = bb.center;
      center = bb.center;
      const [sx, sy, sz] = bb.size;
      const hx = sx / 2,
        hy = sy / 2,
        hz = sz / 2;
      const m = n.fullMatrix;
      const corners = [
        [cx - hx, cy - hy, cz - hz],
        [cx + hx, cy - hy, cz - hz],
        [cx - hx, cy + hy, cz - hz],
        [cx + hx, cy + hy, cz - hz],
        [cx - hx, cy - hy, cz + hz],
        [cx + hx, cy - hy, cz + hz],
        [cx - hx, cy + hy, cz + hz],
        [cx + hx, cy + hy, cz + hz],
      ].map((corner) => (m ? transformPoint(m, corner) : corner));

      for (const [x, y, z] of corners) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        minZ = Math.min(minZ, z);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        maxZ = Math.max(maxZ, z);
      }

      // NEW — log every property that might indicate visibility/type/layer
      contributingNodes.push({
        name: n.name,
        type: n.type,
        visible: n.visible,
        enabled: n.enabled,
        layer: n.layer,
        geometryId: n.geometryId,
        size: { sx, sy, sz },
      });
    }
    (n.children || []).forEach(visit);
  }

  visit(node);

  if (debug) {
    console.table(contributingNodes); // easy to scan for an outlier
  }

  return { minX, minY, minZ, maxX, maxY, maxZ, center, contributingNodes };
}

// export async function prepareHighlight(
//   node,
//   renderer,
//   storeGeometry,
//   texture,
//   highlightMaterial,
// ) {
//   if (!node) return;

//   if (node.geometryId !== undefined) {
//     const geometry = renderer.getGeometry?.(node.geometryId);

//     if (geometry) {
//       const highlightGeometryId = await storeGeometry(
//         geometry,
//         highlightMaterial,
//         texture,
//       );

//       node.userData ??= {};

//       node.userData.normalGeometryId = node.geometryId;

//       node.userData.highlightGeometryId = highlightGeometryId;
//     }
//   }

//   await Promise.all(
//     (node.children || []).map((child) =>
//       prepareHighlight(
//         child,
//         renderer,
//         storeGeometry,
//         texture,
//         highlightMaterial,
//       ),
//     ),
//   );
// }

const highlightGeometryCache = new Map(); // geometryId -> highlightGeometryId

export async function prepareHighlight(
  node,
  renderer,
  storeGeometry,
  texture,
  highlightMaterial,
) {
  if (!node) return;

  if (node.geometryId !== undefined) {
    let highlightGeometryId = highlightGeometryCache.get(node.geometryId);

    if (highlightGeometryId === undefined) {
      const geometry = renderer.getGeometry?.(node.geometryId);
      if (geometry) {
        highlightGeometryId = await storeGeometry(
          geometry,
          highlightMaterial,
          texture,
        );
        highlightGeometryCache.set(node.geometryId, highlightGeometryId);
      }
    }

    if (highlightGeometryId !== undefined) {
      node.userData ??= {};
      node.userData.normalGeometryId = node.geometryId;
      node.userData.highlightGeometryId = highlightGeometryId;
    }
  }

  await Promise.all(
    (node.children || []).map((child) =>
      prepareHighlight(
        child,
        renderer,
        storeGeometry,
        texture,
        highlightMaterial,
      ),
    ),
  );
}
